import React, { useEffect, useMemo, useState } from 'react';
import styles from './AcademicCalendarPage.module.scss';
import ApplicantsTab from '../../Atoms/Tabs/ApplicantsTab';

import Calendar from '../../Molecules/Calendar/Calendar';
import CalendarTable from '../../Molecules/Calendar/CalendarTable';
import { useFetchAcademicYears } from '../../../Hooks/Settings';
import {
  useAddCAlendarPoster,
  useDeleteAcademicYear,
  useFetchCalendarByDay,
  useDeleteCalendarPoster,
  useFetchCalendarPoster,
  useSetAcademicYearAsDefault,
  useFetchAcademicYearsWithCounts,
} from '../../../Hooks/Calendar';
import { debounce, isEmpty } from 'lodash';
import EventModal from '../../Molecules/Events/EventModal';
import UploadPosterModal from '../../Molecules/Events/UploadPosterModal';
import Tabs from './tabs/Tabs';
import AcademicYearsTab from './years-tabs/AcademicYearsTab';
import Loader from '../../Molecules/Loader/Loader';

const AcademicCalendarPage = () => {
  const { data: academicYears, refetch: refetchAcademicYears } =
    useFetchAcademicYears();

  const { mutate: deleteCalendarPoster, isLoading: isDeletingPoster } =
    useDeleteCalendarPoster();

  const { mutate: deleteAcademicYear, isLoading: isDeletingYear } =
    useDeleteAcademicYear();

  const { mutate: setDefaultYear, isLoading: isSettingDefault } =
    useSetAcademicYearAsDefault();

  const [hasPoster, setHasPoster] = useState(false);

  const [selected, setSelected] = useState('general');
  const [selectedYear, setSelectedYear] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [itemToEdit, setItemToEdit] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [uploadModal, setUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('events');

  const [isAppTabSwitching, setIsAppTabSwitching] = useState(false);
  const { data: years, refetch: refetchYearsWithCounts } =
    useFetchAcademicYearsWithCounts(selectedIndex);
  const academicYearsList = useMemo(() => {
    return (
      years?.data?.map((y) => ({
        id: y.acadamicYearId,
        label: y.academicName,
        count: y.calendarCount ?? 0,
        isDefault: y.isDefault,
      })) || []
    );
  }, [years?.data]);

  useEffect(() => {
    if (!academicYearsList.length) return;

    setSelectedYear((prev) => {
      const stillExists = academicYearsList.some((y) => y.id === prev);
      if (prev && stillExists) return prev;

      const def =
        academicYearsList.find((y) => y.isDefault) || academicYearsList[0];

      return def.id;
    });
  }, [academicYearsList]);

  const handleDeleteYear = (year, closeModal) => {
    deleteAcademicYear(year.id, {
      onSuccess: () => {
        closeModal?.();
        refetchAcademicYears();
        refetchAllYears();
      },
    });
  };

  const handleSetDefaultYear = (yearId, closeModal) => {
    setDefaultYear(yearId, {
      onSuccess: () => {
        closeModal?.();
        refetchAcademicYears();
        refetchAllYears();
      },
    });
  };

  const {
    mutate: AddCalendarPoster,
    isSuccess: isSuccesCalendarPoster,
    isLoading: isLoadingCalendarPoster,
  } = useAddCAlendarPoster();

  const {
    data: calendar,
    refetch: refetchCalendar,
    isLoading: isLoadingCalendarByDay,
  } = useFetchCalendarByDay(selectedIndex, selectedYear, searchTerm);

  const { refetch: refetchCalendarPoster } = useFetchCalendarPoster(
    selectedIndex,
    selectedYear,
  );

  useEffect(() => {
    if (isEmpty(selectedYear) || selectedYear === undefined) return;

    let cancelled = false;

    const run = async () => {
      try {
        await Promise.allSettled([
          Promise.resolve(refetchCalendar?.()),
          Promise.resolve(refetchCalendarPoster?.()),
        ]);
      } finally {
        if (!cancelled) setIsAppTabSwitching(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedYear, selectedIndex, refetchCalendar, refetchCalendarPoster]);

  const handleNewClick = () => {
    setIsModalOpen(true);
    setModalMode('add');
  };

  const handleSearch = useMemo(
    () =>
      debounce(() => {
        refetchCalendar();
      }, 300),
    [refetchCalendar],
  );

  useEffect(() => {
    if (!isEmpty(searchTerm)) handleSearch();
  }, [searchTerm, handleSearch]);

  const innerTabs = [
    {
      key: 'poster',
      label: 'ملصق',
      icon: <img src='/icons/poster.svg' alt='' />,
    },
    {
      key: 'events',
      label: 'الفعاليات',
      icon: <img src='/icons/event.svg' alt='' />,
    },
  ];

  const tabs = [
    { index: 0, name: 'general', text: 'عام' },
    { index: 1, name: 'higher-education', text: 'الدراسات العليا' },
    { index: 2, name: 'evening-studies', text: 'الدراسات المسائية' },
    { index: 3, name: 'cadets', text: 'الطلبة المرشحين' },
  ];
  const refetchAllYears = async () => {
    await Promise.allSettled([
      Promise.resolve(refetchAcademicYears?.()),
      Promise.resolve(refetchYearsWithCounts?.()),
    ]);
  };

  const activeYear = useMemo(() => {
    return academicYearsList.find((y) => y.id === selectedYear) || null;
  }, [academicYearsList, selectedYear]);

  const handleDeletePoster = (onDone) => {
    if (selectedYear == null) return;

    deleteCalendarPoster(
      { yearId: selectedYear, type: selectedIndex },
      {
        onSuccess: () => {
          onDone?.();
          refetchCalendarPoster?.();
        },
      },
    );
  };

  return (
    <div className={styles['academicYear-page']}>
      <div className={styles['inner-page']}>
        <div className={styles['years-toggle']}>
          <AcademicYearsTab
            years={academicYearsList}
            activeYearId={selectedYear}
            onSelect={(year) => setSelectedYear(year.id)}
            onDeleteYear={handleDeleteYear}
            isDeleting={isDeletingYear}
            refetchAcademicYears={refetchAllYears}
            onSetDefaultYear={handleSetDefaultYear}
            isSettingDefault={isSettingDefault}
          />
        </div>

        <div className={styles['content']}>
          <div className={styles['tab-container']}>
            {tabs.map((tab) => (
              <ApplicantsTab
                key={tab.index}
                text={tab.text}
                handleOnClick={() => {
                  if (tab.index === selectedIndex) return;

                  setIsAppTabSwitching(true);

                  setSelected(tab.name);
                  setSelectedIndex(tab.index);
                }}
                active={tab.name === selected}
                shadowed={false}
              />
            ))}
          </div>

          <div
            className={styles['search-container']}
            style={{
              borderTopLeftRadius: '0px',
              borderTopRightRadius: '0px',
            }}
          >
            <div className={styles['years']}>
              <div className={styles['years-title']}>
                {activeYear?.isDefault && (
                  <span className={styles.tag}>افتراضي</span>
                )}
                <p>{activeYear?.label ?? ''}</p>
              </div>
              <p className={styles['years-desc']}>
                إدارة ملصق التقويم والفعاليات لهذه السنة الدراسية
              </p>
            </div>

            <Tabs
              tabs={innerTabs}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
          </div>

          <div className={styles['body-container']}>
            {activeTab === 'events' &&
              (isAppTabSwitching || isLoadingCalendarByDay ? (
                <Loader />
              ) : (
                <CalendarTable
                  data={calendar?.data}
                  setIsModalOpen={setIsModalOpen}
                  setModalMode={setModalMode}
                  setItem={setItemToEdit}
                  refetchCalendar={refetchCalendar}
                  itemToEdit={itemToEdit}
                  isLoading={isLoadingCalendarByDay}
                  handleNewClick={handleNewClick}
                />
              ))}

            {activeTab === 'poster' && (
              <Calendar
                type={selectedIndex}
                year={selectedYear}
                isSuccesCalendarPoster={isSuccesCalendarPoster}
                isLoadingCalendarPoster={isLoadingCalendarPoster}
                handleUploadPoster={() => setUploadModal(true)}
                handleDelete={handleDeletePoster}
                isDeletingPoster={isDeletingPoster}
                onPosterStateChange={(exists) => setHasPoster(exists)}
              />
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <EventModal
          mode={modalMode}
          title='الفعالية'
          handleOnClose={() => (setIsModalOpen(false), setItemToEdit({}))}
          refetchCalendar={refetchCalendar}
          itemToEdit={itemToEdit}
          setItem={setItemToEdit}
          selectedIndex={selectedIndex}
          selectedYear={selectedYear}
        />
      )}

      {uploadModal && (
        <UploadPosterModal
          handleOnClose={() => (setUploadModal(false), setItemToEdit({}))}
          refetch={refetchCalendarPoster}
          type={selectedIndex}
          AddCalendarPoster={AddCalendarPoster}
          isLoading={isLoadingCalendarPoster}
          selectedYear={selectedYear}
        />
      )}
    </div>
  );
};

export default AcademicCalendarPage;
